import type { ReactNode } from 'react';
import * as React from 'react';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    H2Emphasized,
    View,
    YStack,
} from 'smoothie';

import { CardColor, CardForm, type EmbossName } from '../../../types/Card';
import { AddToAppleWalletButton } from '../../cards/AddToAppleWalletButton';
import FruitfulCard, { DummyFruitfulCard } from '../../cards/FruitfulCard';

interface CardSuccessProps {
    cardholderName: string;
    cardColor: CardColor;
    buttonText: string;
    headerText: string;
    description: string;
    cardForm: CardForm;
    onPress: () => void;
}

interface CardSuccessAddToWalletProps {
    cardholderName: string;
    cardColor: CardColor;
    headerText: string;
    description: string;
    cardForm: CardForm;
    cardId: string;
    embossName: EmbossName;
    lastFour: string;
    onError: () => void;
    onSuccess: () => void;
}

const TwoOffsetCards = ({
    physicalCardColor,
    virtualCardColor,
    physicalCardholderName,
    virtualCardholderName,
}: {
    physicalCardColor: CardColor;
    virtualCardColor: CardColor;
    physicalCardholderName: string;
    virtualCardholderName: string;
}) => {
    return (
        <View
            transform={[{ translateX: -40 }, { scale: 1.0 }]}
            marginBottom={36}
        >
            <View
                transform={[
                    { translateX: 30 },
                    { translateY: 135 },
                    { rotate: '7.78deg' },
                ]}
                zIndex={1}
            >
                <DummyFruitfulCard
                    form={CardForm.VIRTUAL}
                    showFormLabel={false}
                    color={physicalCardColor}
                    cardHolderName={physicalCardholderName}
                />
            </View>
            <View
                transform={[
                    { translateX: -20 },
                    { translateY: -115 },
                    { rotate: '-10.69deg' },
                    { scale: 0.85 },
                ]}
            >
                <DummyFruitfulCard
                    form={CardForm.PHYSICAL}
                    color={virtualCardColor}
                    showFormLabel={false}
                    cardHolderName={virtualCardholderName}
                />
            </View>
        </View>
    );
};

export const CardSuccessAddToWallet = ({
    cardholderName,
    cardColor,
    description,
    headerText,
    cardId,
    embossName,
    lastFour,
    onError,
    onSuccess,
}: CardSuccessAddToWalletProps) => {
    return (
        <BaseCardSuccess
            description={description}
            headerText={headerText}
            cardComponent={
                <TwoOffsetCards
                    physicalCardColor={cardColor}
                    physicalCardholderName={cardholderName}
                    virtualCardColor={CardColor.GREEN}
                    virtualCardholderName={cardholderName}
                />
            }
        >
            <AddToAppleWalletButton
                onError={onError}
                onSuccess={onSuccess}
                onProgress={() => {
                    // do nothing
                }}
                card={{
                    id: cardId,
                    embossName,
                    lastFour,
                }}
            />
        </BaseCardSuccess>
    );
};

const CardSuccess = ({
    cardholderName,
    cardColor,
    buttonText,
    headerText,
    description,
    cardForm,
    onPress,
}: CardSuccessProps) => {
    return (
        <BaseCardSuccess
            description={description}
            headerText={headerText}
            cardComponent={
                <FruitfulCard
                    cardholderName={cardholderName}
                    form={cardForm}
                    color={cardColor}
                    width={334}
                    marginTop={24}
                    marginBottom={40}
                />
            }
        >
            <Button
                onPress={onPress}
                size={ButtonSize.LARGE}
                variant={ButtonVariant.PRIMARY}
                text={buttonText}
            />
        </BaseCardSuccess>
    );
};

const BaseCardSuccess = ({
    description,
    headerText,
    children,
    cardComponent,
}: Omit<
    CardSuccessProps,
    'buttonText' | 'onPress' | 'cardholderName' | 'cardForm' | 'cardColor'
> & {
    children: ReactNode;
    cardComponent: ReactNode;
}) => {
    return (
        <YStack flex={1} justifyContent="space-between">
            <YStack flexGrow={1} marginHorizontal={16} alignItems="center">
                {cardComponent}
                <H2Emphasized
                    textAlign="center"
                    marginTop={30}
                    marginBottom={16}
                    whiteSpace="pre-wrap"
                >
                    {headerText}
                </H2Emphasized>

                <BodyMediumRegular
                    color="$gray1000"
                    textAlign="center"
                    whiteSpace="pre-wrap"
                >
                    {description}
                </BodyMediumRegular>
            </YStack>
            <View backgroundColor="$white" marginTop={16}>
                {children}
            </View>
        </YStack>
    );
};

export default CardSuccess;
